/**
 * As peças que aparecem em mais de uma tela da gestão.
 *
 * Existem aqui, e não copiadas em cada tela, porque as três que importam
 * carregam regra de produto e não só marcação:
 *
 *  - `Taxa` é o único lugar que decide o que mostrar quando não há
 *    denominador. Se cada tela decidisse, uma delas escreveria `0%`.
 *  - `MarcaDeEditada` é a marca de §10. Dado corrigido em silêncio é dado que
 *    ninguém pode mais auditar, e uma marca reimplementada em três lugares é
 *    uma marca que some de um deles.
 *  - `FilaDeEnvios` é o aviso de §9. Fila que cresce em silêncio é a pior
 *    versão de um caminho degradado.
 *
 * Nenhuma delas tem cor literal: tudo vem das classes de `componentes.css` e
 * de `gestao.css`, que consomem `var(--…)`.
 */

import { useState } from 'react';

import { ROTULO_DE_ESTADO } from '../../servicos/gestao';
import type { EstadoResposta } from '../../tipos';
import { num, porcento, TRACO } from '../../util/formato';

/* ══ Números ═════════════════════════════════════════════════════════════ */

/**
 * Contagem. `null` vira `—` com tom próprio: o traço não pode ser lido como
 * valor, e "ainda não chegou" não é zero.
 */
export function Numero({ valor }: { valor: number | null | undefined }) {
  const texto = num(valor);
  return <span className={texto === TRACO ? 'sem-valor' : undefined}>{texto}</span>;
}

/**
 * Taxa a partir de uma FRAÇÃO (0–1), que é como `0006` devolve.
 *
 * `null` é `—`, nunca `0%`. O SQL preserva de propósito a diferença entre
 * "ninguém confirmou" e "ninguém foi convidado"; transformar em zero aqui
 * apagaria justamente a diferença que ele guardou.
 */
export function Taxa({ fracao }: { fracao: number | null | undefined }) {
  const texto = porcento(fracao);
  return <span className={texto === TRACO ? 'sem-valor' : undefined}>{texto}</span>;
}

export function Kpi({
  rotulo,
  children,
  apoio,
}: {
  rotulo: string;
  children: React.ReactNode;
  apoio?: string;
}) {
  return (
    <div className="kpi">
      <span className="kpi__rotulo">{rotulo}</span>
      <span className="kpi__valor">{children}</span>
      {apoio ? <span className="kpi__apoio">{apoio}</span> : null}
    </div>
  );
}

/* ══ Estado e marcas ═════════════════════════════════════════════════════ */

/** Cor MAIS rótulo, sempre: estado nunca é comunicado só por cor. */
const TOM_DO_ESTADO: Readonly<Record<EstadoResposta, string>> = {
  concluida: 'etiqueta--ok',
  em_andamento: 'etiqueta--info',
  abandonada: 'etiqueta--atencao',
};

export function EstadoDaResposta({ estado }: { estado: EstadoResposta }) {
  return <span className={`etiqueta ${TOM_DO_ESTADO[estado]}`}>{ROTULO_DE_ESTADO[estado]}</span>;
}

/**
 * A marca de §10. Não é decoração: é o que separa "o convidado respondeu isto"
 * de "alguém do painel corrigiu isto depois". Sem ela, a segunda vira a
 * primeira e ninguém consegue mais auditar.
 */
export function MarcaDeEditada({ editada }: { editada: boolean }) {
  if (!editada) return null;
  return (
    <span
      className="etiqueta etiqueta--atencao"
      title="Um valor desta resposta foi corrigido no painel"
    >
      Editada
    </span>
  );
}

/* ══ Erro ════════════════════════════════════════════════════════════════ */

/**
 * A recusa da RLS chega aqui já traduzida por `mensagemDoErro`. O que esta
 * peça garante é que ela **apareça**: erro engolido vira tela branca, e tela
 * branca é o que faz alguém concluir que o produto está quebrado quando o que
 * aconteceu foi o banco fazer exatamente o seu trabalho.
 */
export function AvisoDeErro({
  mensagem,
  aoTentarDeNovo,
}: {
  mensagem: string;
  aoTentarDeNovo?: () => void;
}) {
  return (
    <p className="aviso aviso--erro" role="alert">
      <span className="aviso__rotulo">Não deu</span>
      <span className="aviso__corpo">
        {mensagem}
        {aoTentarDeNovo ? (
          <>
            {' '}
            <button
              type="button"
              className="botao botao--discreto botao--compacto"
              onClick={aoTentarDeNovo}
            >
              Tentar de novo
            </button>
          </>
        ) : null}
      </span>
    </p>
  );
}

/* ══ Carregando ══════════════════════════════════════════════════════════ */

/** Esqueleto com a FORMA da tabela. Giro no meio da tela não diz o que vem. */
export function EsqueletoDeTabela({ colunas, linhas = 5 }: { colunas: number; linhas?: number }) {
  return (
    <div className="esqueleto-tabela" aria-hidden="true">
      {Array.from({ length: linhas }, (_, l) => (
        <div className="esqueleto-tabela__linha" key={l}>
          {Array.from({ length: colunas }, (_, c) => (
            <span className="esqueleto esqueleto--linha" key={c} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ══ §9: a fila que espera transporte ════════════════════════════════════ */

/**
 * Hoje o `acao_email` enfileira e não envia — é decisão, não pendência
 * esquecida (§9). Mas decisão que a tela não conta vira surpresa: a
 * organizadora acharia que os e-mails saíram.
 *
 * Por canal porque "12 pendentes" não diz se o que travou foi e-mail ou
 * webhook, e a providência é diferente para cada um.
 */
export function FilaDeEnvios({
  pendentes,
  falhos,
  porCanal,
}: {
  pendentes: number;
  falhos: number;
  porCanal: Readonly<Record<string, number>>;
}) {
  if (pendentes === 0 && falhos === 0) return null;

  const canais = Object.entries(porCanal);
  return (
    <p className={`aviso ${falhos > 0 ? 'aviso--erro' : 'aviso--atencao'}`}>
      <span className="aviso__rotulo">Aguardando transporte</span>
      <span className="aviso__corpo">
        {pendentes > 0 ? (
          <>
            <strong data-numerico>{num(pendentes)}</strong>{' '}
            {pendentes === 1 ? 'mensagem enfileirada' : 'mensagens enfileiradas'} e ainda não
            entregues
            {canais.length > 0 ? (
              <> ({canais.map(([canal, total]) => `${canal}: ${num(total)}`).join(', ')})</>
            ) : null}
            .{' '}
          </>
        ) : null}
        {falhos > 0 ? (
          <>
            <strong data-numerico>{num(falhos)}</strong> {falhos === 1 ? 'falhou' : 'falharam'} no
            envio.{' '}
          </>
        ) : null}
        O transporte de e-mail ainda não está ligado nesta instalação: a mensagem fica gravada com
        assunto e corpo prontos, e sai quando alguém conectar o serviço de envio.
      </span>
    </p>
  );
}

/* ══ Copiar o link ═══════════════════════════════════════════════════════ */

type EstadoDaCopia = 'repouso' | 'copiado' | 'falhou';

/**
 * O botão que a organizadora mais usa depois de publicar.
 *
 * A área de transferência falha sem alarde em contexto não seguro (`http://`)
 * e em navegador com a permissão negada — e um botão que não faz nada e não
 * diz nada é pior que botão nenhum. Quando falha, o endereço aparece
 * selecionável, que é a saída que sempre funciona.
 */
export function CopiarLink({ link, rotulo = 'Copiar link' }: { link: string; rotulo?: string }) {
  const [estado, definirEstado] = useState<EstadoDaCopia>('repouso');

  const copiar = () => {
    void navigator.clipboard
      ?.writeText(link)
      .then(() => definirEstado('copiado'))
      .catch(() => definirEstado('falhou'));
    if (!navigator.clipboard) definirEstado('falhou');
  };

  return (
    <span className="copiar">
      <button type="button" className="botao botao--compacto" onClick={copiar}>
        {estado === 'copiado' ? 'Copiado' : rotulo}
      </button>
      {/* `aria-live` para quem não vê a troca do rótulo do botão. */}
      <span className="so-leitor" role="status">
        {estado === 'copiado' ? `Link copiado: ${link}` : ''}
      </span>
      {estado === 'falhou' ? (
        <label className="copiar__saida">
          <span className="so-leitor">Endereço do formulário, para copiar à mão</span>
          <input
            className="campo__controle"
            readOnly
            value={link}
            onFocus={(e) => e.target.select()}
          />
        </label>
      ) : null}
    </span>
  );
}
